import { Injectable, signal, inject, computed } from '@angular/core';
import { map, Observable, of, switchMap, tap } from 'rxjs';
import { SelectOptionsInterface } from '@ui-kit';
import { PermissionService } from './permission.service';
import {
  AdminVendorListResponseDto,
  ApprovalStatus,
  OrganizationsApiService,
  UserDto,
  UserRole,
  UsersApiService,
} from '@shared/data-access';
import { HasOrganizationRolePipe, HasOrganizationUserRolePipe } from '../pipes';

const PENDING_VENDOR_STATUSES: ApprovalStatus[] = [
  ApprovalStatus.PendingApproval,
  ApprovalStatus.PendingConfiguration,
];

@Injectable()
export class UserService {
  private userApiService = inject(UsersApiService);
  private permissionService = inject(PermissionService);
  private organizationsApiService = inject(OrganizationsApiService);

  private hasOrganizationRolePipe = new HasOrganizationRolePipe();
  private hasOrganizationUserRole = new HasOrganizationUserRolePipe();

  currentUser = signal<UserDto | null>(null);
  isAdmin = signal<boolean>(false);
  isAccountOwner = signal<boolean>(false);
  isMerchantOwner = signal<boolean>(false);
  isBrandOwner = signal<boolean>(false);
  isAmbassadorOwner = signal<boolean>(false);
  isAccountUser = signal<boolean>(false);
  vendorList = signal<SelectOptionsInterface[]>([]);
  allVendors = signal<AdminVendorListResponseDto[]>([]);

  // Orgs still pending approval/configuration can't be targeted yet — omit them.
  selectableVendorList = computed(() => {
    const hiddenIds = new Set(
      this.allVendors()
        .filter((vendor) =>
          PENDING_VENDOR_STATUSES.includes(vendor.approvalStatus),
        )
        .map((vendor) => vendor.id),
    );
    return this.vendorList().filter((option) => !hiddenIds.has(option.id));
  });

  getUserProfile(): Observable<UserDto> {
    return this.userApiService.getUserProfile().pipe(
      tap((user) => this.updateUserState(user)),
      switchMap((user) =>
        user?.isAdmin ? this.loadVendorList().pipe(map(() => user)) : of(user),
      ),
    );
  }

  loadVendorList(): Observable<void> {
    return this.organizationsApiService.getAdminVendorList().pipe(
      tap((vendors) => {
        this.allVendors.set(vendors);
        const options = vendors.map(({ id, name, approvalStatus }) => ({
          id,
          name:
            approvalStatus !== ApprovalStatus.Configured
              ? `${name} <span class="text-danger">(${approvalStatus})</span>`
              : name,
        }));
        this.vendorList.set(options);
      }),
      map(() => void 0),
    );
  }

  private updateUserState(user: UserDto): void {
    this.currentUser.set(user);
    this.permissionService.setPermissions(user.permissions);
    this.isAccountOwner.set(this.hasOrganizationRolePipe.transform(user.roles));
    this.isAccountUser.set(this.hasOrganizationUserRole.transform(user.roles));
    this.isAdmin.set(user.isAdmin ?? false);
    this.isMerchantOwner.set(
      user.roles.includes(UserRole.MerchantAccountOwner),
    );
    this.isBrandOwner.set(
      user.roles.includes(UserRole.DistributorAccountOwner) ||
        user.roles.includes(UserRole.BrandAccountOwner),
    );
    this.isAmbassadorOwner.set(
      user.roles.includes(UserRole.AmbassadorAccountOwner),
    );
  }
}
